(ns ui.account-selector
  "Filterable account combo box, grouped by social media platform.
   Keyboard interaction and ARIA markup follow the W3C ARIA Authoring
   Practices Guide combobox pattern (editable combobox with list
   autocomplete): DOM focus stays on the text input while ArrowUp/ArrowDown
   move a visual highlight tracked via `aria-activedescendant`.
   Shared between `/ui/agenda` (via `app.agenda.home/schedule-shell`) and
   `/ui/bulk`."
  (:require [app.bundle-social.platforms :as social-platforms]
            [clojure.string :as str]
            [ui.icons :as icons]))

(defn- platform-icon
  [kind classes]
  (if-let [platform (social-platforms/by-key kind)]
    ((:social-platform/icon platform) classes)
    (case kind
      :tiktok (icons/tiktok-logo classes)
      :youtube (icons/youtube-logo classes)
      (icons/instagram-logo classes))))

(defn- platform-label
  [kind]
  (if-let [platform (social-platforms/by-key kind)]
    (:social-platform/account-noun platform)
    (case kind
      :tiktok "TikTok account"
      :youtube "YouTube channel"
      "Instagram account")))

(defn- platform-color-classes
  [kind]
  (if-let [platform (social-platforms/by-key kind)]
    (:social-platform/text-class platform)
    (case kind
      :tiktok ["text-gray-900"]
      :youtube ["text-red-600"]
      ["text-pink-500"])))

(def ^:private group-order
  (into [:instagram :reels :tiktok :youtube]
        (map :social-platform/key)
        social-platforms/platforms))

(def ^:private default-group-labels
  (into {:instagram "Instagram Stories and Reels"
         :reels "Reels"
         :tiktok "TikTok"
         :youtube "YouTube"}
        (map (juxt :social-platform/key :social-platform/name))
        social-platforms/platforms))

(def ^:private listbox-dom-id "account-combobox-listbox")

(def ^:private trigger-dom-id "account-combobox-trigger")

(defn- option-dom-id
  [id]
  (str "account-combobox-option-" id))

(defn- group-dom-id
  [kind]
  (str "account-combobox-group-" (name kind)))

(defn not-connected?
  "True when `account` lost its connection. Handles both account shapes:
   normalized `:schedule/accounts` entries carry `:account/not-connected?`,
   legacy `:instagram/users` entries only `:instagram.account/not-connected`."
  [account]
  (boolean (or (:account/not-connected? account)
               (:instagram.account/not-connected account))))

(defn connected?
  [account]
  (not (not-connected? account)))

(defn- account-fields
  [user]
  {:id (or (:account/key user)
           (:instagram.user/id user))
   :name (or (:account/name user)
             (:instagram.user/name user))
   :pic-url (or (:account/profile-picture-url user)
                (:instagram.user/profile-picture-url user))
   :not-connected? (not-connected? user)
   :kind (or (:account/kind user) :instagram)})

(defn- grouped-accounts
  [accounts group-labels]
  (let [by-kind (group-by :kind accounts)
        labels (merge default-group-labels group-labels)
        kinds (concat group-order
                      (remove (set group-order) (keys by-kind)))]
    (keep (fn [kind]
            (when-let [members (seq (get by-kind kind))]
              {:group/kind kind
               :group/label (or (get labels kind)
                                (str/capitalize (name kind)))
               :group/accounts members}))
          kinds)))

(defn- close-actions []
  [[:store/dissoc ::open?]
   [:store/dissoc ::search-input]
   [:store/dissoc ::active-id]])

(defn- open-actions
  [selected]
  (cond-> [[:store/assoc ::open? true]]
    selected
    (conj [:store/assoc ::active-id (:id selected)])))

(defn- select-actions
  [click-actions-fn id]
  (into (close-actions)
        (click-actions-fn id)))

(defn- focus-trigger!
  "Moves focus back to the trigger button. Per the APG, closing the popup
   with the keyboard returns focus to the combobox. Dispatching renders
   synchronously, so the trigger usually exists right away; the
   requestAnimationFrame covers a throttled (deferred) render."
  []
  (if-let [node (js/document.getElementById trigger-dom-id)]
    (.focus node)
    (js/requestAnimationFrame
      (fn []
        (some-> (js/document.getElementById trigger-dom-id)
                (.focus))))))

(defn- scroll-option-into-view!
  [id]
  (when id
    (js/requestAnimationFrame
      (fn []
        (some-> (js/document.getElementById (option-dom-id id))
                (.scrollIntoView #js {:block "nearest"}))))))

(defn- open!
  "Opens the popup with the selected account highlighted and scrolled
   into view. Called from event handlers only - dispatching from
   life-cycle hooks would trigger a render while rendering."
  [state selected]
  ((:replicant/dispatch! state) {:actions (open-actions selected)})
  (scroll-option-into-view! (:id selected)))

(defn moved-active-id
  "Returns the id of the option `delta` steps away from `active-id` in
   `flat-accounts`, wrapping around. With no current active option,
   ArrowDown starts at the first and ArrowUp at the last option."
  [flat-accounts active-id delta]
  (let [ids (mapv :id flat-accounts)
        n (count ids)
        idx (first (keep-indexed (fn [i id]
                                   (when (= id active-id) i))
                                 ids))]
    (when (pos? n)
      (nth ids (if idx
                 (mod (+ idx delta) n)
                 (if (pos? delta) 0 (dec n)))))))

(defn- input-keydown-fn
  "Keyboard interaction of the open combobox per the APG:
   - ArrowDown/ArrowUp move the visual highlight (with wrap-around)
   - Enter accepts the highlighted option (or the first match while
     filtering) and closes the popup
   - Escape and Alt+ArrowUp close the popup and return focus
   - Tab closes the popup and lets focus move on"
  [{:keys [state flat-accounts active-id query click-actions-fn]}]
  (fn [e]
    (let [dispatch! (:replicant/dispatch! state)
          dispatch-actions! (fn [actions]
                              (dispatch! {:actions actions}))
          key (.-key e)]
      (case key
        "ArrowDown"
        (do (.preventDefault e)
            (when-let [id (moved-active-id flat-accounts active-id 1)]
              (dispatch-actions! [[:store/assoc ::active-id id]])
              (scroll-option-into-view! id)))

        "ArrowUp"
        (do (.preventDefault e)
            (if (.-altKey e)
              (do (dispatch-actions! (close-actions))
                  (focus-trigger!))
              (when-let [id (moved-active-id flat-accounts active-id -1)]
                (dispatch-actions! [[:store/assoc ::active-id id]])
                (scroll-option-into-view! id))))

        "Enter"
        (do (.preventDefault e)
            (let [active-in-list? (some #(= (:id %) active-id) flat-accounts)
                  choice-id (or (when active-in-list? active-id)
                                (when (seq query)
                                  (:id (first flat-accounts))))]
              (dispatch-actions! (if choice-id
                                   (select-actions click-actions-fn choice-id)
                                   (close-actions)))
              (focus-trigger!)))

        "Escape"
        (do (.preventDefault e)
            (dispatch-actions! (close-actions))
            (focus-trigger!))

        ;; No preventDefault, but the focus-trigger! is still required:
        ;; the dispatch renders synchronously and removes this input, so
        ;; without it the browser's default Tab would continue from
        ;; <body> instead of from the combo box.
        "Tab"
        (do (dispatch-actions! (close-actions))
            (focus-trigger!))

        nil))))

(defn- trigger-keydown-fn
  "ArrowDown/ArrowUp (and Alt+ArrowDown) on the closed combobox open the
   popup, highlighting the currently selected account."
  [state selected]
  (fn [e]
    (let [key (.-key e)]
      (when (or (= key "ArrowDown")
                (= key "ArrowUp"))
        (.preventDefault e)
        (open! state selected)))))

(defn- avatar
  [{:keys [name pic-url not-connected?]} size-classes]
  [:div {:class (into ["rounded-full" "overflow-hidden" "bg-gray-200"
                       "shrink-0"]
                      (cond-> size-classes
                        not-connected? (conj "opacity-50")))}
   (if pic-url
     [:img {:src pic-url
            :class ["w-full" "h-full" "object-cover"]}]
     [:div {:class ["w-full" "h-full" "flex" "items-center"
                    "justify-center" "text-xs" "text-gray-400"]}
      (-> name first str .toUpperCase)])])

(defn- option-row
  [{:keys [account selected-id active-id click-actions-fn]}]
  (let [{:keys [id name not-connected?]} account
        selected? (= id selected-id)
        active? (= id active-id)]
    [:button
     {:type "button"
      :role "option"
      :id (option-dom-id id)
      :aria-selected (str selected?)
      :tabindex "-1"
      :class (into ["flex" "w-full" "items-center" "gap-2.5" "px-3"
                    "py-2" "text-left" "text-sm" "transition-colors"
                    "cursor-pointer"]
                   (cond
                     (and selected? active?)
                     ["bg-indigo-100" "text-indigo-700"]

                     selected?
                     ["bg-indigo-50" "text-indigo-700"]

                     active?
                     ["bg-gray-100" "text-gray-700"]

                     :else
                     ["text-gray-700" "hover:bg-gray-50"]))
      :replicant/key id
      :on {:click (select-actions click-actions-fn id)}}
     (avatar account ["w-6" "h-6"])
     [:span {:class ["truncate" "flex-1" "min-w-0"]
             :title name}
      name]
     (when not-connected?
       [:span {:title "Disconnected"}
        (icons/warning-triangle ["w-4" "h-4" "text-red-500" "shrink-0"])])
     (when selected?
       (icons/checkmark ["w-4" "h-4" "shrink-0"]))]))

(defn- group-section
  [{:group/keys [kind label accounts]} selected-id active-id click-actions-fn]
  [:div
   {:replicant/key kind
    :role "group"
    :aria-labelledby (group-dom-id kind)
    :class ["pb-1"]}
   [:div
    {:id (group-dom-id kind)
     :class ["flex" "items-center" "gap-1.5" "px-3" "pt-2" "pb-1"
             "text-xs" "font-semibold" "uppercase" "tracking-wide"
             "text-gray-400"]}
    [:span {:class (platform-color-classes kind)}
     (platform-icon kind ["w-3.5" "h-3.5"])]
    label]
   (map (fn [account]
          (option-row {:account account
                       :selected-id selected-id
                       :active-id active-id
                       :click-actions-fn click-actions-fn}))
        accounts)])

;; z-index tiers: the popup (z-40) and its backdrop (z-30) sit above
;; page content like the agenda story-card badges (z-20), but below
;; fixed chrome like toasts and the onboarding sidebar toggle (z-50).

(defn- dropdown-panel
  [{:keys [groups selected-id active-id click-actions-fn
           add-action add-title add-label]}]
  [:div
   {:class ["absolute" "z-40" "mt-1" "w-full" "min-w-[16rem]" "bg-white"
            "border" "border-gray-200" "rounded-lg" "shadow-lg"
            "max-h-80" "overflow-y-auto"]}
   (if (empty? groups)
     [:div {:class ["px-3" "py-3" "text-sm" "text-gray-400"]}
      "No matches"]
     [:div
      {:id listbox-dom-id
       :role "listbox"
       :aria-label "Social accounts"
       :class ["divide-y" "divide-gray-200"]}
      (map #(group-section % selected-id active-id click-actions-fn)
           groups)])
   [:div
    {:class ["border-t" "border-gray-200"]}
    [:button
     {:type "button"
      :class ["flex" "w-full" "items-center" "gap-2.5" "px-3" "py-2"
              "text-left" "text-sm" "text-gray-500" "hover:bg-gray-50"
              "cursor-pointer"]
      :title add-title
      :on {:click (into (close-actions) add-action)}}
     [:div {:class ["w-6" "h-6" "rounded-full" "border" "border-dashed"
                    "border-gray-300" "flex" "items-center" "justify-center"
                    "text-gray-400" "text-sm" "shrink-0"]}
      "+"]
     add-label]]])

(defn reconnect-banner
  "Amber banner prompting the user to reconnect a specific Instagram
   account. Returns nil when `user` is nil or connected, so the caller
   can place this directly in a hiccup tree."
  [user]
  (when (and user (:instagram.account/not-connected user))
    (let [name (or (:account/name user)
                   (:instagram.user/name user))]
      [:div
       {:class ["flex" "items-center" "gap-3" "px-4" "py-3" "rounded-lg"
                "bg-amber-50" "border" "border-amber-200"]}
       [:div
        {:class ["flex-1" "min-w-0"]}
        [:p
         {:class ["text-sm" "font-medium" "text-amber-800"]}
         (str "@" name " is disconnected.")]
        [:p
         {:class ["text-xs" "text-amber-600" "mt-0.5"]}
         "Reconnect this account to schedule and publish stories."]]
       [:button
        {:class ["shrink-0" "inline-flex" "items-center" "px-3" "py-1.5"
                 "text-xs" "font-medium" "text-amber-800" "bg-amber-100"
                 "hover:bg-amber-200" "rounded-md" "transition-colors"
                 "border" "border-amber-300" "cursor-pointer"]
         :on {:click [[:connect3-info/open
                       {:username name
                        ;; the connection to reconnect, so connect3
                        ;; keeps its settings.dat and device; `true`
                        ;; (a new connect to the server) when unknown
                        :reconnect (or (:instagram.account/reconnect user) true)
                        :source "account-selector"}]]}}
        "Reconnect"]])))

(defn render
  "Render the account combo box.

   Accounts are grouped by platform (Instagram stories, Reels, TikTok,
   YouTube); groups without members are not shown. Opening the combo box
   focuses a text input that filters accounts by name. Keyboard
   interaction follows the APG combobox pattern: ArrowDown/ArrowUp move
   the highlight, Enter selects, Escape closes.

   Options:
   - :state - app state map (used for the combo box open/search state)
   - :users - seq of account maps with :account/{kind,key,name,profile-picture-url}
              or legacy :instagram.user/{id,name,profile-picture-url}
   - :selected-id - :account/key (or legacy :instagram.user/id) of the
                    currently selected account
   - :click-actions-fn - (fn [user-id]) returning the action chain to
                         dispatch when an account is selected
   - :group-labels - optional map of :account/kind to group label, merged
                     over the defaults (e.g. {:instagram \"Instagram story\"})
   - :add-action - action chain for the '+ Add account' footer row.
                   Defaults to [[:connect3-info/open {:source \"account-selector\"}]].
   - :add-title - hover title for the footer row.
   - :add-label - visible label for the footer row."
  [{:keys [state users selected-id click-actions-fn group-labels
           add-action add-title add-label]
    :or {add-action [[:connect3-info/open {:source "account-selector"}]]
         add-title "Connect another Instagram account"
         add-label "Add account"}}]
  (let [accounts (map account-fields users)
        selected (some #(when (= (:id %) selected-id) %) accounts)
        open? (::open? state)
        active-id (::active-id state)
        input-value (or (::search-input state) "")
        query (str/trim (str/lower-case input-value))
        filtered (if (seq query)
                   (filter (fn [{:keys [name]}]
                             (str/includes? (str/lower-case (or name ""))
                                            query))
                           accounts)
                   accounts)
        groups (grouped-accounts filtered group-labels)
        flat-accounts (vec (mapcat :group/accounts groups))
        active (some #(when (= (:id %) active-id) %) flat-accounts)]
    [:div
     {:class ["relative" "w-full" "max-w-xs"]}
     (if open?
       [:div
        {:class ["relative" "z-40" "flex" "items-center" "h-10" "rounded-lg"
                 "border" "border-indigo-300" "bg-white" "ring-2"
                 "ring-indigo-100"]}
        [:div
         {:class ["absolute" "left-0" "top-0" "bottom-0" "w-9" "flex"
                  "items-center" "justify-center" "text-gray-400"
                  "pointer-events-none"]}
         (icons/search ["w-4" "h-4"])]
        [:input
         (cond-> {:type "text"
                  :role "combobox"
                  :aria-expanded "true"
                  :aria-controls listbox-dom-id
                  :aria-autocomplete "list"
                  :aria-label "Search social accounts"
                  :autocomplete "off"
                  :placeholder (or (:name selected) "Search accounts…")
                  :value input-value
                  :replicant/on-mount (fn [{:keys [replicant/node]}]
                                        (.focus node))
                  :class ["w-full" "h-full" "pl-9" "pr-3" "rounded-lg"
                          "text-sm" "bg-transparent" "outline-none"
                          "border-0"]
                  :on {:input [[:store/assoc ::search-input
                                :event/target.value]
                               [:store/dissoc ::active-id]]
                       :keydown (input-keydown-fn
                                  {:state state
                                   :flat-accounts flat-accounts
                                   :active-id (:id active)
                                   :query query
                                   :click-actions-fn click-actions-fn})}}
           active
           (assoc :aria-activedescendant (option-dom-id (:id active))))]]
       [:button
        {:type "button"
         :id trigger-dom-id
         :aria-haspopup "listbox"
         :aria-expanded "false"
         :class ["flex" "w-full" "items-center" "gap-2" "h-10" "px-3"
                 "rounded-lg" "border" "border-gray-300" "bg-white"
                 "text-sm" "font-medium" "text-gray-700"
                 "hover:bg-gray-50" "cursor-pointer"
                 "transition-colors" "shadow-sm"]
         :title "Select account"
         :on {:click (fn [_e]
                       (open! state selected))
              :keydown (trigger-keydown-fn state selected)}}
        [:div
         {:class ["flex" "items-center" "gap-2" "flex-1" "min-w-0"]}
         (if selected
           [:div
            {:class ["flex" "items-center" "gap-2" "min-w-0"]}
            (avatar selected ["w-6" "h-6"])
            [:span {:class ["truncate" "min-w-0"]
                    :title (:name selected)}
             (:name selected)]
            [:span {:class (into ["shrink-0"]
                                 (platform-color-classes (:kind selected)))
                    :title (platform-label (:kind selected))}
             (platform-icon (:kind selected) ["h-3.5" "w-3.5"])]
            (when (:not-connected? selected)
              [:span {:title "Disconnected"}
               (icons/warning-triangle ["w-4" "h-4" "text-red-500"
                                        "shrink-0"])])]
           [:span {:class ["text-gray-400"]}
            "Select account…"])]
        (icons/chevron-down
          (into ["w-4" "h-4" "text-gray-400" "shrink-0" "transition-transform"]
                (when open? ["rotate-180"])))])
     (when open?
       [:div
        {:class ["fixed" "inset-0" "z-30"]
         :aria-hidden "true"
         :on {:click (close-actions)}}])
     (when open?
       (dropdown-panel
         {:groups groups
          :selected-id selected-id
          :active-id (:id active)
          :click-actions-fn click-actions-fn
          :add-action add-action
          :add-title add-title
          :add-label add-label}))]))
